import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex";
import styled, { DefaultTheme } from "styled-components";
import BarChart, { BarChartTooltipContext } from "common/Charts/BarChart";
import type { ScriptableContext } from "chart.js";
import useUsageChart from "@shared/features/settings/subscription/hooks/useUsageChart";
import { LanguageNameSpaces, RawLineChartDataPoint } from "@shared/types";
import { formatTimeSeriesChartData } from "@shared/utils/charts";
import { useMemo } from "react";
import Tooltip from "common/Tooltips/Tooltip";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { hexToRGB } from "@shared/utils/misc";
import { formatDate } from "@shared/utils/date";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { escapeHtml, safeCssColor } from "utils/html";

const UsageChart = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const {
    theme,
    isLoading,
    numOpened,
    numMissed,
    tradeUsageChartData,
    legends,
    backgroundColors,
  } = useUsageChart();

  const parsedData = useMemo(() => {
    const formattedData: RawLineChartDataPoint[] = (
      tradeUsageChartData || []
    ).map((point) => [point[0], ...point[1]]);

    const openedTradesData = formatTimeSeriesChartData(
      formattedData,
      1,
      false,
      undefined,
      0
    );
    const missedTradesData = formatTimeSeriesChartData(
      formattedData,
      2,
      false,
      undefined,
      0
    );
    // New look: opened trades in the accent, missed ones as a quiet grey
    // cap on the same bar (colour is kept for meaning).
    const colors = theme.isNewLook
      ? [newLookOpenedColor(theme), newLookMissedColor(theme)]
      : backgroundColors;
    // New look: thinner bars with 3px top corners on the top segment only.
    const newLookBar = theme.isNewLook
      ? {
          barPercentage: 0.58,
          categoryPercentage: 1,
          maxBarThickness: 28,
          borderSkipped: false,
        }
      : {};
    const topRadius = (hasCapAbove: boolean) =>
      theme.isNewLook
        ? {
            borderRadius: (ctx: ScriptableContext<"bar">) => {
              const missed = (
                ctx.chart.data.datasets[1]?.data[ctx.dataIndex] as
                  | { y?: number }
                  | undefined
              )?.y;
              const covered = hasCapAbove ? false : !!missed;
              const r = covered ? 0 : 3;
              return { topLeft: r, topRight: r, bottomLeft: 0, bottomRight: 0 };
            },
          }
        : {};
    return [
      {
        label: legends[0],
        data: openedTradesData,
        backgroundColor: colors[0],
        ...newLookBar,
        ...topRadius(false),
      },
      {
        label: legends[1],
        data: missedTradesData,
        backgroundColor: colors[1],
        ...newLookBar,
        ...topRadius(true),
      },
    ];
  }, [tradeUsageChartData, theme]);

  if (theme.isNewLook) {
    return (
      <Wrapper>
        <NewLookLegend>
          <NewLookLegendItem>
            <NewLookSwatch $isMissedTrades={false} />
            {t("plan_usage.opened_trades")}
            <NewLookLegendValue>{numOpened}</NewLookLegendValue>
          </NewLookLegendItem>
          <Tooltip
            title={t("tooltips.trades_missed")}
            showMobileBottomDrawer={true}
            mobileTitle={t("plan_usage.trades_missed")}
          >
            <NewLookLegendItem>
              <NewLookSwatch $isMissedTrades={true} />
              {t("plan_usage.trades_missed")}
              <NewLookLegendValue>{numMissed}</NewLookLegendValue>
            </NewLookLegendItem>
          </Tooltip>
        </NewLookLegend>
        <BarChart
          key={theme.isDarkMode ? "dark" : "light"}
          data={parsedData}
          isStacked={true}
          precision={0}
          loading={isLoading}
          loadingBgColor="transparent"
          numOfXTicks={5}
          adjustOptions={(options) => newLookAxes(theme, options)}
          tooltipHtml={(context) => newLookTooltip(theme, context)}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Flex $justifyContent="flex-end" style={{ marginBottom: 8 }}>
        <LegendText>
          <Circle /> {t("plan_usage.opened_trades")}:{" "}
          <span style={{ fontWeight: 600, marginLeft: 4 }}>{numOpened}</span>
        </LegendText>
        <Tooltip
          title={t("tooltips.trades_missed")}
          showMobileBottomDrawer={true}
          mobileTitle={t("plan_usage.trades_missed")}
        >
          <LegendText style={{ marginLeft: 20 }}>
            <Circle $isMissedTrades={true} /> {t("plan_usage.trades_missed")}:{" "}
            <span
              style={{
                fontWeight: 600,
                color: theme.colors.red,
                marginLeft: 4,
              }}
            >
              {numMissed}
            </span>
          </LegendText>
        </Tooltip>
      </Flex>
      <BarChart
        data={parsedData}
        isStacked={true}
        precision={0}
        loading={isLoading}
        loadingBgColor="transparent"
      />
    </Wrapper>
  );
};

export default UsageChart;

const newLookOpenedColor = (theme: DefaultTheme) =>
  hexToRGB(getNewLookTokens(theme).accent, theme.isDarkMode ? 0.9 : 0.82);
const newLookMissedColor = (theme: DefaultTheme) =>
  getNewLookTokens(theme).hairlineStrong;

/** Quiet axes: no axis lines, dashed hairline gridlines, three or four ticks. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const newLookAxes = (theme: DefaultTheme, options: any) => {
  const tokens = getNewLookTokens(theme);
  const font = { size: 11, weight: 400, family: theme.typography.fontFamily };
  const { x, y } = options.scales;
  return {
    ...options,
    layout: { padding: { top: 4 } },
    scales: {
      x: {
        ...x,
        ticks: { ...x.ticks, color: tokens.muted, font, padding: 8 },
        border: { display: false },
        grid: { display: false },
      },
      y: {
        ...y,
        ticks: {
          ...y.ticks,
          count: undefined,
          maxTicksLimit: 4,
          color: tokens.muted,
          font,
          padding: 12,
        },
        border: { display: false, dash: [3, 4] },
        grid: { ...y.grid, drawTicks: false, color: tokens.hairline },
      },
    },
  };
};

/**
 * Date, then opened / missed with their swatches. An HTML string (the chart
 * sets it as the tooltip's innerHTML), so the data in it is escaped.
 */
const newLookTooltip = (
  theme: DefaultTheme,
  context: BarChartTooltipContext
) => {
  const tokens = getNewLookTokens(theme);
  const points = context.tooltip?.dataPoints || [];
  const rawOf = (point?: (typeof points)[number]) =>
    point?.raw as { x?: string; y?: number } | undefined;
  const rows = points
    .map(
      (point) => `
      <div style="display:flex;align-items:center;gap:8px;margin-top:6px;font-size:12px;line-height:16px;color:${
        tokens.secondary
      }">
        <span style="width:8px;height:8px;border-radius:2px;background:${safeCssColor(
          point.dataset.backgroundColor
        )}"></span>
        <span style="flex:1;padding-right:16px">${escapeHtml(
          point.dataset.label
        )}</span>
        <span style="font-weight: 500;color:${tokens.ink}">${escapeHtml(
        rawOf(point)?.y ?? 0
      )}</span>
      </div>`
    )
    .join("");
  return `<div style="min-width:150px">
    <div style="font-size:11px;line-height:14px;color:${
      tokens.muted
    }">${escapeHtml(
    formatDate(rawOf(points[0])?.x, false, false)
  )}</div>${rows}</div>`;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;
const Circle = styled.div<{ $isMissedTrades?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(p) => {
    if (p.$isMissedTrades) {
      return p.theme.isDarkMode
        ? p.theme.colors.secondaryGrey
        : p.theme.colors.primaryGrey;
    }
    return p.theme.colors.primaryBlue;
  }};
  margin-right: 6px;
  margin-bottom: 1px;
`;
const LegendText = styled(Text).attrs({ $size: "S", as: "div" })`
  font-weight: 500;
  display: flex;
  align-items: center;
`;
const NewLookLegend = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-bottom: 8px;
`;
const NewLookLegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const NewLookSwatch = styled.span<{ $isMissedTrades: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${(p) =>
    p.$isMissedTrades
      ? newLookMissedColor(p.theme)
      : newLookOpenedColor(p.theme)};
`;
const NewLookLegendValue = styled.span`
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-variant-numeric: tabular-nums;
`;
